<!-- Draws the Basket page showing the user what they have selected. To start, this component gets the Booking ID from the booking-storage store, then calls the API to get the booking details. The booking-store store is persistently saved to localstorage so the page can be refreshed and the order details kept. -->
<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { handleError } from "@/services/AlertService";
import type { Ref } from "vue";
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { IGQLBookingOverview, IGQLLeg, IGQLTripsOverview, ITicketKey } from "@/models/graphql/BookingOverviewResponse";
import type { TicketDetailsByKey, FaresByKeyModel, TicketRestrictionSummary } from "@/models/JourneySelectorViewModel";
import { getFareForFareKey } from "@/utilities/FareFromKey";
import { JourneyViewType } from "@/models/basket/JourneyViewType";
import TicketDetails from "@/components/3-Basket/TicketDetails.vue";
import DeliveryMethod from "@/components/3-Basket/DeliveryMethod.vue";
import { getTicketDetails, getRestrictionDetails } from "@/utilities/GetTicketDetails";
import OrderCostSummary from "@/components/3-Basket/OrderCostSummary.vue";
import type { DeliveryMethods } from "@/models/basket/DeliveryMethods";
import ReservationsModal from "@/components/3-Basket/ReservationsModal.vue";
import { useBookingData } from "@/stores/booking-storage";
import BasketLifetime from "@/components/3-Basket/BasketLifetime.vue";
import { getBooking } from "@/graphql/functions/getBooking";
import { deleteTripFromBooking } from "@/graphql/functions/deleteTripFromBooking";
import { deleteBooking } from "@/graphql/functions/deleteBooking";
import _get from "lodash/get";
import { isMobile } from "@/utilities/IsMobileHelper";
import { getFormattedDurationString } from "@/utilities/getFormattedDurationString";
import { getUniqueListOfTickets } from "@/utilities/getUniqueListOfTickets";
import { getTimeFromDateTimeIso } from "@/utilities/getTimeFromDateTimeIso";
import { DateTime } from "luxon";

const bookingData: Ref<IGQLBookingOverview | null> = ref(null);
const journeyTypeSelection: Ref<JourneyViewType> = ref(JourneyViewType.Outbound);
const deliveryMethod: Ref<DeliveryMethods | string> = ref("");
const reservationDetails: Ref<IGQLLeg | null> = ref(null);
const ticketDetails: Ref<TicketDetailsByKey | null> = ref(null);
const fareDetails: Ref<FaresByKeyModel | null> = ref(null);
const firstOrigin: Ref<string> = ref("");
const { width } = useWindowSize();
const route = useRoute();
const router = useRouter();
const td: Ref = ref(null);
const rm: Ref = ref(null);
const isLoading: Ref<boolean> = ref(false);
const bookingID: Ref<string | null> = ref(null);
const ticketRestrictions: Ref<TicketRestrictionSummary | null> = ref(null);
const bookingStore = useBookingData();

function formatDate(date: string | undefined) {
    return date ? DateTime.fromISO(date).toFormat("dd LLLL HH:mm") : "Invalid date";
}

function addToBasket() {
    const { bookingId } = route.query;
    router.push({ path: "/", query: { bookingId } });
}

async function getBookingDetails() {
    isLoading.value = true;
    try {
        bookingData.value = await getBooking(bookingID!.value!);
        isLoading.value = false;
    } catch (error) {
        // If API returned a BOOKING_DOES_NOT_EXISTS error, ensure the booking data is cleared
        if (_get(error, "networkError.result.errors", false)) {
            for (const gqlError of error.networkError.result.errors) {
                if (gqlError.extensions?.code === "BOOKING_DOES_NOT_EXISTS") {
                    bookingID.value = null;
                    bookingData.value = null;
                    bookingStore.bookingData = null;
                }
            }
        } else {
            handleError(error);
        }
        isLoading.value = false;
    }

    // If the you cannot collect your ToD tickets from the origin station, set a variable to be the name of the origin station. This is used in DeliveryMethod.
    if (bookingData.value?.todCollectionPossibleFromOrigin === false) {
        const firstOriginFromTrips = bookingData.value?.trips.toSorted((x: IGQLTripsOverview, y: IGQLTripsOverview) => {
            const date1 = new Date(x.outboundJourney.legs[0].departing);
            const date2 = new Date(y.outboundJourney.legs[0].departing);
            return date1.getTime() - date2.getTime();
        });
        firstOrigin.value = firstOriginFromTrips![0].outboundJourney.originName!;
    }
}

async function performDeleteTripFromBooking(tripId: string) {
    isLoading.value = true;
    try {
        await deleteTripFromBooking(bookingID.value!, tripId);
        await getBookingDetails();
        isLoading.value = false;
    } catch (error) {
        isLoading.value = false;

        // If API returned a BOOKING_DOES_NOT_EXIST_ERROR error, clear the booking ID because the customer has removed the last item from it.
        if (_get(error, "networkError.result.errors", false)) {
            for (const gqlError of error.networkError.result.errors) {
                if (gqlError.extensions?.code === "BOOKING_DOES_NOT_EXISTS") {
                    bookingID.value = null;
                    bookingData.value = null;
                    bookingStore.bookingData = null;
                }
            }
        } else {
            handleError(error);
        }
    }
}

// Completely delete the current booking session entirely
async function clearBasket() {
    try {
        isLoading.value = true;
        await deleteBooking(bookingID!.value!);
        bookingData.value = null;
        bookingStore.bookingData = null;
        isLoading.value = false;
    } catch (error) {
        isLoading.value = false;
        handleError(error);
    }
}

function toggleTicketDetail(selectedTicketTicket: ITicketKey, item: IGQLTripsOverview, journeyType: JourneyViewType) {
    const fareKey = selectedTicketTicket.fareKey as string;
    const fareDetailsKey = getFareForFareKey(fareKey, item.fares);
    const ticketDetail = getTicketDetails(bookingData?.value!, fareDetailsKey);
    journeyTypeSelection.value = journeyType;
    fareDetails.value = fareDetailsKey!;
    ticketDetails.value = ticketDetail!.value!;
    const restrictionKey = fareDetails.value.restrictionCode;
    if (restrictionKey !== "") {
        ticketRestrictions.value = getRestrictionDetails(bookingData.value!, restrictionKey);
    }
    td.value.showDialog();
}

async function toggleReservationsModal(reservations: IGQLLeg) {
    reservationDetails.value = reservations;
    rm.value.showDialog();
}

function setDeliveryMethod(method: DeliveryMethods | string) {
    deliveryMethod.value = method;
}

function formatSeatLabel(leg: IGQLLeg): string {
    let formattedLabel: string = "";
    if (leg.reservations.length > 0) {
        const countedPlace = leg.reservations.filter((r) => r.type === "COUNTED_SEAT");
        if (countedPlace.length > 0) {
            formattedLabel = leg.reservations.length > 1 ? `${leg.reservations.length} reservations` : `${leg.reservations.length} reservation`;
        } else {
            formattedLabel = leg.reservations.length > 1 ? `${leg.reservations.length} seats` : `${leg.reservations.length} seat`;
        }
    }
    return formattedLabel;
}

onMounted(() => {
    bookingID.value = bookingStore.checkExistingBooking();
    if (bookingID.value) {
        getBookingDetails();
    } else {
        bookingData.value = null;
    }
});
</script>

<template>
    <div class="your-basket-wrapper pa-3 pa-sm-6 pb-5">
        <v-overlay :model-value="isLoading" contained class="align-center justify-center" persistent>
            <v-progress-circular indeterminate :size="70" :width="7" color="primary"> </v-progress-circular>
        </v-overlay>

        <v-empty-state icon="mdi-basket-outline" title="Your basket is empty" class="pt-10 pb-12" v-if="bookingData === null">
            <template v-slot:text>
                <p>Looks like there are no journeys in your basket.</p>
                <v-btn class="mb-3 mt-5 mx-auto button text-white" size="large" rounded="lg" @click="$router.push({ name: 'home' })">Start a new search</v-btn>
            </template>
        </v-empty-state>

        <div>
            <div v-if="bookingData !== null && bookingData?.trips.length > 0">
                <h2 class="title pb-5">Basket <BasketLifetime :booking-data="bookingData" style="vertical-align: baseline"> </BasketLifetime></h2>
                <div class="top-ctas-wrapper">
                    <v-btn @click="clearBasket()" class="mb-sm-1 mb-sm-5 clear-basket" flat prepend-icon="mdi-delete"> Empty basket </v-btn>
                    <v-btn @click="addToBasket()" class="mb-sm-5 add-basket" variant="outlined" flat prepend-icon="mdi-plus"> Add journey </v-btn>
                </div>
                <h2 v-if="isMobile()" class="mt-3 mb-1">Journeys</h2>
            </div>

            <div class="pt-4 pt-sm-10" v-if="bookingData !== null && bookingData?.trips.length > 0">
                <!-- DESKTOP - Draw a table at larger resolutions -->
                <v-table v-if="width > 760" class="pb-5">
                    <thead>
                        <tr>
                            <th class="text-left">Journey</th>
                            <th class="text-left">Type</th>
                            <th class="text-left">Tickets</th>
                            <th class="text-left">Date</th>
                            <th class="text-left">Seats</th>
                            <th class="text-left">Cost</th>
                            <th class="text-left"></th>
                        </tr>
                    </thead>
                    <tbody v-for="(item, i) in bookingData?.trips" v-bind:key="i">
                        <tr>
                            <td>{{ item.outboundJourney.originName }} to {{ item.outboundJourney.destinationName }}</td>
                            <td>
                                <span v-if="item.inboundJourney !== null">Return</span>
                                <span v-else-if="item.isOpenReturn">Open Return</span>
                                <span v-else>Single</span>
                            </td>
                            <td class="ticket-list">
                                <a href="#" v-for="(ticket, j) in getUniqueListOfTickets(item.outboundTickets)" :key="j" @click="toggleTicketDetail(ticket, item, JourneyViewType.Outbound)">
                                    {{ getFareForFareKey(ticket.fareKey, item.fares)?.displayName }}
                                </a>
                            </td>
                            <td>{{ formatDate(item.outboundJourney.departing) }}</td>
                            <td>
                                <div v-for="(leg, i) in item.outboundJourney.legs" :key="i">
                                    <a href="#" @click="toggleReservationsModal(leg)">{{ formatSeatLabel(leg) }}</a>
                                </div>
                            </td>
                            <td>{{ item.totalPriceFormatted }}</td>
                            <td>
                                <v-btn @click="performDeleteTripFromBooking(item.id)" class="delete" size="small"> <font-awesome-icon icon="fa-trash" class="pr-3"></font-awesome-icon>Delete </v-btn>
                            </td>
                        </tr>
                        <tr v-if="item.inboundJourney !== null">
                            <td>{{ item.inboundJourney.originName }} to {{ item.inboundJourney.destinationName }}</td>
                            <td></td>
                            <td class="ticket-list">
                                <a href="#" v-for="(ticket, j) in getUniqueListOfTickets(item.inboundTickets)" :key="j" @click="toggleTicketDetail(ticket, item, JourneyViewType.Inbound)">
                                    {{ getFareForFareKey(ticket.fareKey, item.fares).displayName }}
                                </a>
                            </td>
                            <td>{{ formatDate(item.inboundJourney.departing) }}</td>
                            <td>
                                <div v-for="(leg, i) in item.inboundJourney?.legs" :key="i">
                                    <a href="#" @click="toggleReservationsModal(leg)">{{ formatSeatLabel(leg) }}</a>
                                </div>
                            </td>
                        </tr>
                        <td colspan="7"><v-divider v-if="bookingData?.trips && i !== bookingData.trips.length - 1"></v-divider></td>
                    </tbody>
                </v-table>

                <!-- MOBILE - Use expansion panels instead on mobiles -->
                <div v-else v-for="(item, i) in bookingData?.trips" v-bind:key="i" class="w-100 mb-3">
                    <v-expansion-panels>
                        <v-expansion-panel :eager="true">
                            <v-expansion-panel-title hide-actions>
                                <div>
                                    {{ item.outboundJourney.originName }}
                                    <font-awesome-icon v-if="item.inboundJourney !== null" icon="fa-exchange" class="pl-2 pr-2"></font-awesome-icon>
                                    <font-awesome-icon v-else icon="fa-arrow-right" class="pl-2 pr-2"></font-awesome-icon>
                                    {{ item.outboundJourney.destinationName }}
                                </div>
                                <div>{{ item.totalPriceFormatted }}</div>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <h3>Outward Journey</h3>
                                <div class="journey-summary">
                                    <div class="outbound">
                                        <p>Departing {{ getTimeFromDateTimeIso(item.outboundJourney.departing) }}</p>
                                    </div>
                                    <div class="inbound">
                                        <p>Arriving {{ getTimeFromDateTimeIso(item.outboundJourney.arriving) }}</p>
                                    </div>
                                </div>

                                <a href="#" v-for="(ticket, j) in item.outboundTickets" :key="j" @click="toggleTicketDetail(ticket, item, JourneyViewType.Outbound)"> {{ getFareForFareKey(ticket.fareKey, item.fares)?.displayName }}.</a> {{ item.outboundJourney.changes }} changes, {{ getFormattedDurationString(item.outboundJourney.totalTimeIso) }}.

                                <div v-if="item.inboundJourney !== null">
                                    <h3 class="pt-5">Return Journey</h3>
                                    <div class="journey-summary">
                                        <div class="outbound">
                                            <p>Departing {{ getTimeFromDateTimeIso(item.inboundJourney.departing) }}</p>
                                        </div>
                                        <div class="inbound">
                                            <p>Arriving {{ getTimeFromDateTimeIso(item.inboundJourney.arriving) }}</p>
                                        </div>
                                    </div>

                                    <a href="#" v-for="(ticket, j) in item.inboundTickets" :key="j" @click="toggleTicketDetail(ticket, item, JourneyViewType.Inbound)"> {{ getFareForFareKey(ticket.fareKey, item.fares)?.displayName }}.</a> {{ item.inboundJourney.changes }} changes, {{ getFormattedDurationString(item.inboundJourney.totalTimeIso) }}.
                                </div>

                                <div class="d-flex align-center justify-space-between mt-5">
                                    <p>{{ item.adults }} Adult<span v-if="item.adults !== 1">s</span>, {{ item.children }} child<span v-if="item.children !== 1">ren</span></p>

                                    <v-btn @click="performDeleteTripFromBooking(item.id)" class="delete" size="small"> <font-awesome-icon icon="fa-trash" class="pr-3"></font-awesome-icon>Delete </v-btn>
                                </div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>

                <DeliveryMethod :delivery-methods="bookingData?.availableFulfilmentMethods" :collection-from-origin="bookingData?.todCollectionPossibleFromOrigin" @selectedDelivery="setDeliveryMethod($event)" :firstOriginStation="firstOrigin" class="mt-sm-10"> </DeliveryMethod>
            </div>
        </div>
        <div v-if="bookingData !== null" class="pt-sm-10 pb-10 order-cost-summary">
            <OrderCostSummary :ticket-total-formatted="bookingData?.bookingAmountFormatted" :booking-fee-formatted="bookingData?.bookingFeeFormatted" :total-payment-formatted="bookingData?.totalPaymentAmountFormatted" :booking-id="bookingID!" :delivery-method="deliveryMethod"></OrderCostSummary>
        </div>
        <TicketDetails ref="td" :ticket-detail="ticketDetails!" :journey-fare="fareDetails" :fare-type="journeyTypeSelection" :restrictionDetails="ticketRestrictions"></TicketDetails>
        <ReservationsModal ref="rm" :leg="reservationDetails!"></ReservationsModal>
    </div>
</template>

<style scoped lang="scss">
@import "@/assets/base.scss";

.v-empty-state {
    grid-column: 1/3;
    padding-left: 0;
    padding-right: 0;
}

.your-basket-wrapper {
    display: grid;
    grid-template-columns: 1fr 25%;
    background-color: white;
    column-gap: 25px;
}

.your-basket-wrapper > .order-cost-summary {
    grid-column: 2/2;
    grid-row: 1/1;
}

.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > td,
.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > th {
    border-bottom: none;
}

:deep(.v-empty-state .v-btn) {
    background-color: var(--vt-c-orange);
}

.top-ctas-wrapper {
    display: flex;
    flex-wrap: wrap;
    row-gap: 11px;
    column-gap: 8px;
}

.v-overlay {
    background-color: rgba(255, 255, 255, 0.9);
    min-height: 70vh;
}

.v-overlay :deep(.v-overlay__scrim) {
    background: none;
}

.v-table__wrapper > table > tbody > tr > td {
    // Need to overwrite an !important from vuetify :(
    height: 35px !important;
}

thead {
    font-size: 1.2rem;
}

.delete {
    background-color: var(--vt-c-blue-link);
    color: var(--vt-c-white);
    text-transform: capitalize;
}

.clear-basket {
    background-color: var(--vt-light-gray);
    color: var(--vt-c-black);
}

.add-basket {
    color: var(--vt-c-orange);
}

tbody {
    font-size: 1rem;
}

:deep(table) {
    border-top: 2px solid var(--vt-c-lightgray);
    border-bottom: 2px solid var(--vt-c-lightgray);
}

.ticket-list span::after {
    content: ", ";
}

.ticket-list span:last-child::after {
    content: "";
}

.v-row.pt-10.pb-10.pr-10 {
    padding-left: 1.5rem;
}

@media screen and (max-width: $mobile-breakpoint) {
    .v-empty-state {
        grid-column: 1/2;
    }

    .your-basket-wrapper {
        grid-template-columns: 100%;
        grid-template-rows: auto auto;
    }

    .your-basket-wrapper > .order-cost-summary {
        grid-column: 1/1;
        grid-row: 2/2;
    }

    .journey-summary {
        svg.svg-inline--fa.fa-arrow-right.pr-3 {
            left: 70%;
        }

        .outbound {
            float: left;
            display: inline-grid;
            width: 50%;
            text-align: left;
        }

        .inbound {
            float: right;
            display: inline-grid;
            width: 50%;
            text-align: right;
        }
    }

    .v-expansion-panel-text__wrapper {
        padding: 20px 30px 20px;
    }
}

@media (min-width: $mobile-breakpoint) {
    .v-col:last-child {
        max-width: 100%;
        width: 100%;
    }
}

@media (min-width: 1400px) {
    .your-basket-wrapper {
        max-width: 95%;
        margin: 0 auto;
    }
}

@media (min-width: 1500px) {
    .your-basket-wrapper {
        max-width: 1500px;
        margin: 0 auto;
    }
}
</style>
